<script setup lang="ts">
/**
 * Shadow panel of an image placeholder. Everything that is not specific to an image lives in
 *  useShadowSettings + ShadowSettings: this only says which element to write into, and when an image
 *  can't cast a shadow.
 */
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import ShadowSettings from '@/components/DesignStudio/ShadowSettings.vue';
import { useShadowSettings } from '@/composables/shadowSettings';

const props = withDefaults(
    defineProps<{
        canvas: CanvasEx;
        imageElement?: ImagePlaceholderElement;
        editMode?: boolean;
        cmykMode?: boolean;
        isPreview?: boolean;
        exportFormat?: string;
    }>(),
    {
        imageElement: undefined,
        isPreview: false
    }
);

const emit = defineEmits<{
    (e: 'shadow-change'): void;
    (e: 'open-shadow-color-library-selector'): void;
}>();

const shadow = useShadowSettings<ImagePlaceholderElement>({
    element: () => props.imageElement,
    canvas: () => props.canvas,
    editMode: () => props.editMode,
    exportFormat: () => props.exportFormat,
    // Same restriction the outline already has: a soft shadow can't survive being traced to a
    //  single flat color by potrace, so it only applies to full color and grayscale images
    supportsShadow: (element) => !!element.fullColor || !!element.grading,
    openLibrarySelector: () => emit('open-shadow-color-library-selector'),
    onShadowChange: () => emit('shadow-change')
});
</script>

<template>
    <shadow-settings :shadow="shadow" :cmyk-mode="props.cmykMode" :is-preview="props.isPreview" variant="image" />
</template>
